// src/components/validationNotes/VisionneusePiece.jsx
// ============================================================================
// Le justificatif d'une demande de rectification, en aperçu
// ============================================================================
//
// Même raisonnement que la visionneuse des justificatifs de caisse
// (ui/VisionneuseJustificatif) : la pièce n'est servie que par l'API, avec le
// jeton dans l'en-tête — un lien ne sait pas le porter — et ouvrir le blob
// dans un onglet après un `await` serait arrêté par les bloqueurs de fenêtres.
// L'aperçu est fiable ; un document n'est pas une fiche, la règle du drill-in
// ne s'y oppose pas. Cette visionneuse-là ne sert pas : elle est attachée aux
// routes du portefeuille.
//
// Le navigateur n'affiche ni le HEIC (photo d'iPhone) ni un type inconnu : on
// le dit, et on propose de télécharger le fichier tel qu'il a été déposé.
// L'adresse du blob est révoquée à la fermeture, sans quoi il reste en mémoire
// tant que l'onglet vit.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Download, FileWarning, Loader2, Paperclip, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { validationNotes } from './apiValidationNotes';
import { tailleFichier } from './presentationNotes';

const IMAGES_LISIBLES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const VisionneusePiece = ({ piece, onFermer }) => {
  const [chargement, setChargement] = useState(false);
  const [lu, setLu] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!piece?.id) return undefined;
    let annule = false;
    let adresse = null;

    setChargement(true);
    setErreur(null);
    setLu(null);
    validationNotes.piece(piece.id)
      .then((reponse) => {
        // Fermée pendant la requête : ne pas rouvrir ce qu'on vient de quitter.
        if (annule) return;
        adresse = URL.createObjectURL(reponse.blob);
        setLu({ ...reponse, url: adresse });
      })
      .catch((e) => {
        if (!annule) setErreur(e.message || 'Justificatif introuvable.');
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });

    return () => {
      annule = true;
      if (adresse) URL.revokeObjectURL(adresse);
    };
  }, [piece?.id]);

  const type = (lu?.type || piece?.type || '').toLowerCase();
  const estImage = IMAGES_LISIBLES.includes(type);
  const estPdf = type === 'application/pdf';
  const nom = piece?.nom_fichier || lu?.nom || 'Justificatif';

  // Un clic sur un lien créé ici, dans le geste de l'utilisateur : le
  // navigateur l'accepte, ce qu'il refuserait à un `window.open` différé.
  const telecharger = () => {
    if (!lu?.url) return;
    const lien = document.createElement('a');
    lien.href = lu.url;
    lien.download = nom;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  };

  return (
    <Dialog open={Boolean(piece)} onOpenChange={(ouvert) => !ouvert && onFermer?.()}>
      <DialogContent
        // Hauteur DÉFINIE : l'iframe d'un PDF ne prend sa hauteur que contre un
        // parent dont la hauteur est connue (voir ui/VisionneuseJustificatif).
        className="flex h-[92vh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0"
        // On lit un document : un clic à côté ne doit pas le faire disparaître.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Paperclip className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="truncate text-lg font-semibold text-gray-900">{nom}</DialogTitle>
            <DialogDescription className="truncate text-sm text-gray-500">
              {['Justificatif de la demande de rectification', tailleFichier(piece?.taille)].filter(Boolean).join(' · ')}
            </DialogDescription>
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden bg-gray-50">
          {chargement && (
            <div role="status" className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin text-[#246990]" />
              <p className="text-sm">Ouverture du justificatif…</p>
            </div>
          )}

          {!chargement && erreur && (
            <div role="alert" className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
              <FileWarning className="h-8 w-8 text-gray-300" />
              <p className="text-sm font-semibold text-gray-600">{erreur}</p>
            </div>
          )}

          {!chargement && !erreur && lu && (
            estImage ? (
              <div className="flex flex-1 items-center justify-center overflow-auto p-3">
                <img src={lu.url} alt={nom} className="max-h-full max-w-full object-contain" />
              </div>
            ) : estPdf ? (
              <iframe src={lu.url} title={nom} className="h-full w-full flex-1 border-0" />
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
                <FileWarning className="h-8 w-8 text-gray-300" />
                <p className="text-sm font-semibold text-gray-600">Aperçu impossible dans le navigateur</p>
                <p className="max-w-md text-[11px] leading-snug text-gray-500">
                  Ce format (photo HEIC d'iPhone, par exemple) ne s'affiche pas ici. Téléchargez le fichier pour
                  l'ouvrir sur ce poste.
                </p>
              </div>
            )
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t bg-gray-50 px-5 py-4 sm:flex-row sm:justify-end">
          <Button variant="outline" size="default" className={BOUTON_PIED} onClick={onFermer}>
            Fermer
          </Button>
          <Button
            size="default"
            className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            disabled={!lu?.url}
            onClick={telecharger}
          >
            <Download className="h-4 w-4" />
            Télécharger
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisionneusePiece;
